import {
  PUBLICATION_REFUSALS,
  type PublicationRefusal,
  type RefusedPublication,
  type RefusedPublicationResolution,
  type RefusedPublicationStorage,
} from '@streamlain/doc-publication-contract';
import type { IDBPTransaction, StoreNames } from 'idb';

import type { IDBConnection } from './db';
import {
  deleteLocalPublicationDocs,
  publicationDocDeleteStores,
} from './publication-doc-delete';
import type { DocStorageSchema, IDBPublicationRecord } from './schema';

type JournalTransaction<Mode extends IDBTransactionMode> = IDBPTransaction<
  DocStorageSchema,
  StoreNames<DocStorageSchema>[],
  Mode
>;

/** Journal entries with a member in docIds, each once, in journal order. */
async function entriesOf(
  tx: JournalTransaction<IDBTransactionMode>,
  docIds: readonly string[]
): Promise<IDBPublicationRecord[]> {
  const entries = new Map<string, IDBPublicationRecord>();
  const byDoc = tx.objectStore('publications').index('docIds');
  for (const docId of new Set(docIds))
    for (const record of await byDoc.getAll(docId))
      entries.set(record.operationId, record);
  return [...entries.values()].sort((a, b) => a.sequence - b.sequence);
}

function storedRefusal(record: IDBPublicationRecord): PublicationRefusal {
  return (
    PUBLICATION_REFUSALS.find(code => code === record.refusal) ??
    'REMOTE_CONFLICT'
  );
}

/** The user's way out of a publication the server refused for good. */
export class IndexedDBRefusedPublicationStorage implements RefusedPublicationStorage {
  constructor(private readonly connection: IDBConnection) {}

  listRefusedPublications(
    docIds: readonly string[]
  ): Promise<readonly RefusedPublication[]> {
    return this.connection.encryption.transaction(
      ['publications'],
      'readonly',
      async tx =>
        (await entriesOf(tx, docIds))
          .filter(record => record.state === 'BLOCKED_CONFLICT')
          .map(record => ({
            operationId: record.operationId,
            docIds: [...record.docIds],
            refusal: storedRefusal(record),
          }))
    );
  }

  resolveRefusedPublications(
    docIds: readonly string[],
    resolution: RefusedPublicationResolution
  ): Promise<readonly string[]> {
    return this.connection.encryption.transaction(
      resolution === 'retry'
        ? ['publications']
        : [...publicationDocDeleteStores],
      'readwrite',
      async tx => {
        const journal = tx.objectStore('publications');
        const entries = await entriesOf(tx, docIds);
        if (resolution === 'retry') {
          const blocked = entries.filter(
            record => record.state === 'BLOCKED_CONFLICT'
          );
          for (const { refusal: _refusal, ...record } of blocked)
            await journal.put({
              ...record,
              state: 'REBASING',
              storageRevision: crypto.randomUUID(),
            });
          return [...new Set(blocked.flatMap(record => record.docIds))];
        }
        // A document whose local bytes go takes every publication it is a
        // member of with it, and that publication's other members follow.
        const affected = new Set(docIds);
        let doomed = entries;
        for (;;) {
          const known = affected.size;
          for (const record of doomed)
            for (const docId of record.docIds) affected.add(docId);
          if (affected.size === known) break;
          doomed = await entriesOf(tx, [...affected]);
        }
        // Own journal references must be gone before deciding key retention.
        for (const record of doomed) await journal.delete(record.operationId);
        await deleteLocalPublicationDocs(tx, [...affected]);
        return [...affected];
      }
    );
  }
}
